import { toHex32, toHex } from '@pactswap/cwap-cm-lib/shared';
import { FEE, OrderBatchFields } from './constants';
/**
 * Expands an orders batch into a flat list of individual order parameters.
 *
 * @param ordersBatch - The orders batch to expand.
 * @returns A flat array of `CreateOrderBaseParams`.
 */
export const ordersBatchToList = (ordersBatch) => {
    return ordersBatch.flatMap((orderSet) => {
        return Array(orderSet[0])
            .fill(orderSet[1])
            .map((order, i) => ({ ...order, ...orderSet[2]?.[i] }));
    }, []);
};
/**
 * Array of fields in CreateOrderBaseParams that contain bigint values.
 * Used for serialization and deserialization of order data.
 * - baseAmount: The amount of base tokens in the order
 * - l1Amount: The amount of L1 tokens in the order
 * - minL1Amount: The minimum acceptable L1 token amount
 */
export const BIGINT_FIELDS = ['baseAmount', 'l1Amount', 'minL1Amount'];
/**
 * Serializes the base template of an order batch.
 * Converts bigint values to 32-byte hex strings.
 * @param base - The base order template.
 * @returns A serialized order template.
 */
const serializeOrderBase = (base) => {
    const template = OrderBatchFields.map((field) => {
        const value = base[field];
        if (!value) {
            return null;
        }
        if (BIGINT_FIELDS.includes(field)) {
            return toHex32(value);
        }
        return value;
    });
    return template;
};
/**
 * Maps order batch field names to their corresponding index positions.
 * This map is used to efficiently look up field indices when serializing order tunes.
 * The indices match the order of fields in OrderBatchFields array.
 */
export const orderBatchFieldToIndexMap = new Map(OrderBatchFields.map((field, index) => [field, index]));
/**
 * Serializes a single tune object from an order batch.
 * Converts tune properties to their corresponding index and serializes bigint values to hex.
 * @param tune - The tune object with partial order parameters.
 * @returns A serialized tune object with indexed fields.
 */
const serializeOrderTune = (tune) => {
    return Object.fromEntries(Object.entries(tune).map(([key, value]) => {
        const index = orderBatchFieldToIndexMap.get(key);
        if (index === undefined) {
            // This case should ideally not be hit with correct types, but it's a useful safeguard.
            throw new Error(`Invalid field in order tune: ${key}`);
        }
        const serializedValue = BIGINT_FIELDS.includes(key)
            ? toHex(value)
            : value;
        return [index, serializedValue];
    }));
};
/**
 * Serializes an array of order batches into a format suitable for smart contract calls.
 *
 * This function processes each batch by:
 * 1. Serializing the base order template, converting amounts to `Hex32` format.
 * 2. Serializing individual order `tunes`, mapping field names to indices and amounts to `Hex` format.
 *
 * This structured approach ensures that the data is compact and correctly formatted for the contract,
 * separating the common base data from the variations in each order.
 *
 * @param ordersBatch - An array of order batches to be serialized.
 * @returns The serialized order batches.
 */
export const serializeOrdersBatch = (ordersBatch) => {
    return ordersBatch.map(([count, base, tunes]) => [count, serializeOrderBase(base), tunes?.map(serializeOrderTune)]);
};
/**
 * Deserializes a serialized order template into a partial order object.
 * It filters out any null values from the template.
 * @param template - The serialized order template.
 * @returns A partial order object with hex values.
 */
export const deserializeOrderTemplate = (template) => {
    return Object.fromEntries(OrderBatchFields.map((field, i) => [field, template[i]]).filter(([, value]) => value !== null));
};
/**
 * Deserializes a serialized order tune into a partial order object.
 * It maps the numeric keys back to their corresponding field names.
 * @param tune - The serialized order tune with numeric keys.
 * @returns A partial order object with hex values.
 */
export const deserializeOrderTune = (tune) => {
    return Object.fromEntries(Object.entries(tune).map(([key, value]) => [OrderBatchFields[Number(key)], value]));
};
/**
 * Converts specific hex string fields in a partial order object to BigInts.
 * It targets fields defined in `BIGINT_FIELDS`.
 * @param order - A partial order object with potential hex values.
 * @returns A partial order object with BigInt values for specified fields.
 */
export const convertHexFieldsToBigInt = (order) => {
    return Object.fromEntries(Object.entries(order).map(([key, value]) => [
        key,
        BIGINT_FIELDS.includes(key) && value ? BigInt(value) : value,
    ]));
};
/**
 * Deserializes a single serialized order set into a list of full order objects.
 * An order set consists of a count, a base template, and optional tunes.
 * @param orderSet - A single serialized order set.
 * @returns An array of fully formed order objects.
 */
export const deserializeOrderSet = (orderSet) => {
    const [count, template, tunes] = orderSet;
    const baseOrder = deserializeOrderTemplate(template);
    const ordersWithHex = Array(count)
        .fill(baseOrder)
        .map((order, i) => ({
        ...order,
        ...(tunes?.[i] && deserializeOrderTune(tunes[i])),
    }));
    return ordersWithHex.map((order) => convertHexFieldsToBigInt(order));
};
/**
 * Deserializes a complete batch of serialized orders into a flat list of order objects.
 * This is the primary function for converting serialized data back into a usable format.
 * @param ordersBatch - The complete serialized orders batch.
 * @returns A flat array of fully formed order objects.
 */
export const deserializeBatchToList = (ordersBatch) => {
    return ordersBatch.flatMap(deserializeOrderSet);
};
/**
 * Calculates various parameters from a list of orders, preparing them for a transaction.
 * This includes total amounts, fees, and counts.
 * @param orders - An array of `CreateOrderBaseParams` objects.
 * @returns An object containing calculated values like total L1 amount, fees, and order count.
 * @throws Will throw an error if an order is missing the `l1Amount`.
 */
export const calculateOrdersListParams = (orders) => {
    const ordersLength = BigInt(orders.length);
    if (!orders.every((order) => order.l1Amount)) {
        throw new Error('calculateOrdersListParams: Order with missing l1Amount');
    }
    const init = { l1Amount: 0n, minL1Amount: 0n, definedBaseAmount: 0n, itemsWithUndefinedBaseAmount: 0n, fee: 0n };
    const { l1Amount, minL1Amount, definedBaseAmount, itemsWithUndefinedBaseAmount, fee } = orders.reduce((result, order) => {
        return {
            l1Amount: result.l1Amount + BigInt(order.l1Amount),
            minL1Amount: result.minL1Amount + BigInt(order.minL1Amount ?? 0),
            definedBaseAmount: result.definedBaseAmount + BigInt(order.baseAmount ?? 0),
            itemsWithUndefinedBaseAmount: result.itemsWithUndefinedBaseAmount + (order.baseAmount ? 0n : 1n),
            fee: result.fee + FEE.CREATE_ORDER(order.maxUses),
        };
    }, init);
    return {
        l1Amount,
        minL1Amount,
        definedBaseAmount,
        itemsWithUndefinedBaseAmount,
        fee,
        isBatch: ordersLength > 1n,
        ordersLength,
    };
};
